import * as FaIcons from "react-icons/fa";
import { translate } from "../../../../utils/Common";
import GroupFormCreate from "./GroupFormCreate";
import { useState } from "react";
import { GiPlatform } from "react-icons/gi";
import { MdEditSquare } from "react-icons/md";
import PageContainer from "../../../../components/layout/PageContainer";
import PageHeader from "../../../../components/layout/PageHeader";
import Button from "../../../../components/common/Button";
import Modal from "../../../../components/common/Modal";
import styles from "./PlatformSettingsForm.module.css";
import { useLocation } from "react-router-dom";
import { verifyAccessViewPermissions } from "../../../../config/permissions";

const HELP_TABS = [
  { key: "step1", icon: FaIcons.FaFileAlt },
  { key: "step2", icon: FaIcons.FaTools },
  { key: "step3", icon: GiPlatform },
  { key: "step4", icon: FaIcons.FaDoorOpen },
];

const CreatePlatform = () => {
  const location = useLocation();
  const [modulesIsCompleted, setModulesIsCompleted] = useState(false);
  const [settingsLocker, setSettingsLocker] = useState({});
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [activeHelpTab, setActiveHelpTab] = useState(0);
  const queryParams = new URLSearchParams(location.search);
  const typeViewFromQuery = queryParams.get("view");
  const extId = queryParams.get("extId");
  const typeView = typeViewFromQuery || (extId ? "edit" : "create");

  // Nota: `/dashboard/platforms/settings` no es un módulo del catálogo BLI.
  // Para permisos, esta pantalla debe heredar del módulo GENERAL.
  const permissionsPath = "/dashboard/platforms/general";
  const canCreate = verifyAccessViewPermissions(["create"], { path: permissionsPath });
  const canEdit =
    verifyAccessViewPermissions(["edit-configuration"], { path: permissionsPath }) ||
    verifyAccessViewPermissions(["update"], { path: permissionsPath });
  const canUpdate = verifyAccessViewPermissions(["update"], { path: permissionsPath });
  const canReorderDoors = verifyAccessViewPermissions(["reorder-doors"], { path: permissionsPath });
  const canDeleteDoors = verifyAccessViewPermissions(["delete"], { path: permissionsPath });
  const canAssignDoorCompanies = verifyAccessViewPermissions(["assign-door-companies"], { path: permissionsPath });
  const canSyncDetails = verifyAccessViewPermissions(["sync-details"], { path: permissionsPath });
  const hasAccess = typeView === "create" ? canCreate : canEdit;

  const handleBackToPlatforms = () => {
    window.location.href = "/dashboard/platforms/general";
  };

  const breadcrumbClick = (e) => {
    e.preventDefault();
    handleBackToPlatforms();
  };

  const hasLockerData = Object.keys(settingsLocker).length > 0;

  const capitalizeFirst = (str) => {
    if (!str || typeof str !== 'string') return str;
    return str.charAt(0).toUpperCase() + str.slice(1);
  };

  const breadcrumbs = [
    <span
      key="lockers"
      onClick={breadcrumbClick}
      className={styles.breadcrumbLink}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === "Enter" && breadcrumbClick(e)}
    >
      {capitalizeFirst(translate("global.lockers"))}
    </span>,
    capitalizeFirst(
      typeView === "create"
        ? translate("global.createSettings")
        : translate("global.editSettings")
    ),
    ...(hasLockerData ? [capitalizeFirst(settingsLocker?.namePlatform || '')] : []),
  ];

  const title = hasLockerData
    ? (settingsLocker?.namePlatform || "-")
    : typeView === "create"
    ? (
        <>
          <FaIcons.FaPlusCircle className="mr-1" style={{ marginRight: "0.25rem" }} />
          {translate("global.createSettings")}
        </>
      )
    : (
        <>
          <MdEditSquare style={{ marginRight: "0.25rem" }} />
          {translate("global.editSettings")}
        </>
      );

  const subtitle = hasLockerData && (
    <div className={styles.subtitleWithBadge}>
      <span>
        {translate("dashboard.platforms.detail.subtitle")
          .replace("{id}", String(settingsLocker?.IdPlataforma ?? "-"))
          .replace("{locationId}", settingsLocker?.externalId || "-")}
      </span>
    </div>
  );

  const logo = hasLockerData ? (
    <div className={styles.lockerLogo}>
      <GiPlatform size={28} />
    </div>
  ) : null;

  const actions = (
    <>
      <Button
        type="button"
        variant="outline"
        onClick={() => { setShowHelpModal(true); setActiveHelpTab(0); }}
        leftIcon={<FaIcons.FaQuestionCircle />}
      >
        {translate("dashboard.platforms.detail.actions.help")}
      </Button>
      <Button
        type="button"
        variant="primary"
        onClick={handleBackToPlatforms}
        leftIcon={<FaIcons.FaArrowLeft />}
      >
        {translate("dashboard.platforms.detail.actions.backToPlatforms")}
      </Button>
    </>
  );

  if (!hasAccess) {
    return (
      <PageContainer>
        <PageHeader
          title={typeView === "create" ? translate("global.createSettings") : translate("global.editSettings")}
          breadcrumbs={breadcrumbs}
          actions={actions}
          icon={<GiPlatform size={28} />}
          divider={false}
        />
        <div className={styles.successAlert} role="alert" style={{ background: "var(--bg-secondary)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <FaIcons.FaLock />
            <span>{translate("dashboard.clients.formSearch.NOT_ACCESS")}</span>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleBackToPlatforms}
          >
            {translate("global.goBack")}
          </Button>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader
        title={title}
        breadcrumbs={breadcrumbs}
        subtitle={subtitle}
        logo={logo}
        actions={actions}
        icon={hasLockerData ? null : <GiPlatform size={28} />}
        divider={!modulesIsCompleted}
      />

      <Modal
        isOpen={showHelpModal}
        onClose={() => setShowHelpModal(false)}
        title={translate("dashboard.platforms.helpModal.title")}
        size="xl"
      >
        <div className={styles.helpModalContent}>
          <p>{translate("dashboard.platforms.helpModal.intro")}</p>
        </div>
        <div className={styles.helpModalTabs}>
          <nav className={styles.helpModalNav} role="tablist" aria-label={translate("dashboard.platforms.helpModal.title")}>
            {HELP_TABS.map((tab, index) => {
              const isActive = index === activeHelpTab;
              const Icon = tab.icon;
              return (
                <button
                  key={tab.key}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  aria-controls={`help-panel-${index}`}
                  id={`help-tab-${index}`}
                  className={isActive ? `${styles.helpModalNavItem} ${styles.helpModalNavItemActive}` : styles.helpModalNavItem}
                  onClick={() => setActiveHelpTab(index)}
                >
                  <Icon size={16} aria-hidden />
                  {translate(`dashboard.platforms.helpModal.${tab.key}.title`)}
                </button>
              );
            })}
          </nav>
          <div
            id={`help-panel-${activeHelpTab}`}
            role="tabpanel"
            aria-labelledby={`help-tab-${activeHelpTab}`}
            className={styles.helpModalPanel}
          >
            <section className={styles.helpModalSection}>
              <h3 className={styles.helpModalSectionTitle}>
                {translate(`dashboard.platforms.helpModal.${HELP_TABS[activeHelpTab].key}.title`)}
              </h3>
              <p>{translate(`dashboard.platforms.helpModal.${HELP_TABS[activeHelpTab].key}.p1`)}</p>
              <p>{translate(`dashboard.platforms.helpModal.${HELP_TABS[activeHelpTab].key}.p2`)}</p>
              {(HELP_TABS[activeHelpTab].key === "step1" || HELP_TABS[activeHelpTab].key === "step2" || HELP_TABS[activeHelpTab].key === "step3") && (
                <>
                  <p>{translate(`dashboard.platforms.helpModal.${HELP_TABS[activeHelpTab].key}.p3`)}</p>
                  <ul className={styles.helpModalFeatureList}>
                    <li>
                      <span className={styles.helpModalFeatureIcon} aria-hidden>
                        <FaIcons.FaSyncAlt />
                      </span>
                      {translate(`dashboard.platforms.helpModal.${HELP_TABS[activeHelpTab].key}.featureSync`)}
                    </li>
                  </ul>
                </>
              )}
              {HELP_TABS[activeHelpTab].key === "step4" && (
                <>
                  <p>{translate("dashboard.platforms.helpModal.step4.p3")}</p>
                  <ul className={styles.helpModalFeatureList}>
                    <li>
                      <span className={styles.helpModalFeatureIcon} aria-hidden>
                        <FaIcons.FaArrowsAlt />
                      </span>
                      {translate("dashboard.platforms.helpModal.step4.featurePosition")}
                    </li>
                    <li>
                      <span className={styles.helpModalFeatureIcon} aria-hidden>
                        <FaIcons.FaColumns />
                      </span>
                      {translate("dashboard.platforms.helpModal.step4.featureColumn")}
                    </li>
                    <li>
                      <span className={styles.helpModalFeatureIcon} aria-hidden>
                        <FaIcons.FaSyncAlt />
                      </span>
                      {translate("dashboard.platforms.helpModal.step4.featureSync")}
                    </li>
                  </ul>
                </>
              )}
            </section>
          </div>
        </div>
      </Modal>

      {modulesIsCompleted && (
        <div className={styles.successAlert} role="alert">
          <div>
            <FaIcons.FaCheck /> {translate("global.allModulesComplete")}. ID{" "}
            {translate("global.platform")} <strong>{settingsLocker?.IdPlataforma}</strong>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => (window.location.href = "/dashboard/platforms/general")}
          >
            {translate("global.close")}
          </Button>
        </div>
      )}

      <GroupFormCreate
        setModulesIsCompleted={setModulesIsCompleted}
        settingsLocker={settingsLocker}
        setSettingsLocker={setSettingsLocker}
        permissions={{
          canCreate,
          canUpdate,
          canReorderDoors,
          canDeleteDoors,
          canAssignDoorCompanies,
          canSyncDetails,
          permissionsPath,
        }}
      />
    </PageContainer>
  );
};

export default CreatePlatform;
